import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { User, Truck, Building, Shield, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

const BG_IMAGE = "https://images.unsplash.com/photo-1771775751121-3091d79073d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMGNoYXVmZmV1ciUyMGNhciUyMGxpbW91c2luZSUyMG5pZ2h0fGVufDB8fHx8MTc3NTQ4MDE1N3ww&ixlib=rb-4.1.0&q=85";

const roleOptions = [
  { role: "admin", icon: Shield, label: "Administrator", description: "System administration & management" },
  { role: "staff", icon: User, label: "Staff", description: "Booking management & dispatch" },
  { role: "driver", icon: Truck, label: "Driver", description: "View assigned jobs & earnings" },
  { role: "client", icon: Building, label: "Client", description: "Book rides & view invoices" }
];

const LoginPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      const routes = { admin: "/admin", staff: "/staff", client: "/client", driver: "/driver" };
      navigate(routes[user.role] || "/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left — Hero Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img src={BG_IMAGE} alt="Luxury chauffeur vehicle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/80" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative z-10 flex flex-col justify-end h-full p-12 pb-16"
        >
          <p className="text-[#C8A95B] uppercase tracking-[0.25em] text-xs font-semibold mb-3">
            Premium Chauffeur Service
          </p>
          <h1 className="text-white text-4xl font-bold leading-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Arrive in Style,<br />Every Time.
          </h1>
          <p className="text-white/60 mt-4 max-w-md text-sm leading-relaxed">
            Limo Near Me delivers executive ground transportation across Australia — airport pickups, corporate travel, and special occasions.
          </p>
        </motion.div>
      </div>

      {/* Right — Role Selection */}
      <div className="w-full lg:w-1/2 bg-[#0b0b0f] flex flex-col items-center justify-center px-6 py-12 relative">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        <div className="w-full max-w-md relative z-10">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <Link to="/" className="inline-block">
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-14 w-auto mx-auto" />
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-center mb-8"
          >
            <h2 className="text-white text-2xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Welcome Back
            </h2>
            <p className="text-white/40 text-sm mt-2">Select your account type to continue</p>
          </motion.div>

          {/* Role Cards */}
          <div className="space-y-3">
            {roleOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <motion.div
                  key={option.role}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.25 + index * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <Link
                    to={`/login/${option.role}`}
                    className="group flex items-center gap-4 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#C8A95B]/30 transition-all duration-300"
                    data-testid={`login-option-${option.role}`}
                  >
                    <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#C8A95B]/10 flex items-center justify-center group-hover:bg-[#C8A95B]/20 transition-colors duration-300">
                      <Icon className="h-5 w-5 text-[#C8A95B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{option.label}</p>
                      <p className="text-white/35 text-xs mt-0.5">{option.description}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/20 group-hover:text-[#C8A95B] group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-10 space-y-3 text-center"
          >
            <div className="flex items-center gap-3 justify-center">
              <span className="h-px flex-1 bg-white/[0.06]" />
              <span className="text-white/25 text-xs uppercase tracking-wider">or</span>
              <span className="h-px flex-1 bg-white/[0.06]" />
            </div>
            <div className="pt-2 space-y-2">
              <Link to="/book" className="inline-block text-[#C8A95B] hover:text-[#dbbf6f] text-sm font-medium transition-colors" data-testid="guest-booking-link">
                Book as Guest
              </Link>
              <p className="text-white/30 text-xs">
                Don't have an account?{" "}
                <Link to="/register" className="text-white/50 hover:text-white transition-colors underline underline-offset-2">
                  Sign up
                </Link>
              </p>
            </div>
          </motion.div>

          <p className="text-center text-[10px] text-white/15 mt-12">
            &copy; 2026 Limo Near Me &middot; Australia Wide Car Services Pty Ltd &middot; ABN 22 680 932 003
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
